const { RECIPES, scaleRecipe, getBrewStage, formatTime, extractionYield } = await import(
  window.HG_ASSETS?.['brew-companion-model.js'] || new URL('./hg16-brew-model.js', import.meta.url).href
);

const STORAGE_KEY = 'hg-brew-companion-v1';
const METHODS = { v60: 'V60', aeropress: 'AeroPress', 'french-press': 'French press', chemex: 'Chemex' };
const TASTES = {
  balanced: 'Keep this recipe. A good cup is worth repeating.',
  strong: 'If the flavour is good but the cup feels too intense, try a little hot water in the finished cup. Taste before making another change.',
  weak: 'For your next brew, try a slightly finer grind while keeping the recipe the same. Taste again before changing anything else.',
  sour: 'Try a slightly finer grind next time, keeping dose, water and method the same. Fruity acidity can be part of the coffee; sharpness alone is not a diagnosis.',
  bitter: 'Try a slightly coarser grind next time and keep everything else the same. Roast character and uneven brewing can also contribute to bitterness or dryness.'
};
const NEUTRAL_ADVICE = 'Taste your coffee when it is comfortable to drink. Choose what you notice, then try one small change next time.';
const text = (value, max = 80) => typeof value === 'string' ? value.slice(0, max).trim() : '';
const knownRecipe = id => RECIPES.find(recipe => recipe.id === id);
const node = (tag, className, content) => {
  const element = document.createElement(tag);
  if (className) element.className = className;
  if (content !== undefined) element.textContent = content;
  return element;
};
const button = (label, handler, quiet = false) => {
  const element = node('button', `bc-button${quiet ? ' bc-button--quiet' : ''}`, label);
  element.type = 'button';
  element.addEventListener('click', handler);
  return element;
};
const profileDefault = () => ({ version: 1, settings: {}, favorites: [], diary: [], lastRecipe: '', active: null });

function safeSetup(value, id) {
  if (!value || typeof value !== 'object') return null;
  const scaled = scaleRecipe(id, value.dose);
  if (!scaled.valid) return null;
  return { dose: scaled.dose, coffee: text(value.coffee), grinder: text(value.grinder), setting: text(value.setting, 40) };
}

function safeSession(value) {
  if (!value || typeof value !== 'object' || !knownRecipe(value.recipeId)) return null;
  const setup = safeSetup(value, value.recipeId);
  if (!setup || !['running', 'paused', 'complete'].includes(value.status)) return null;
  const total = scaleRecipe(value.recipeId, setup.dose).totalSeconds * 1000;
  const elapsedMs = Number(value.elapsedMs);
  if (!Number.isFinite(elapsedMs) || elapsedMs < 0 || elapsedMs > total + 1000) return null;
  const startedAt = Number(value.startedAt);
  if (value.status === 'running' && (!Number.isFinite(startedAt) || startedAt <= 0 || startedAt > Date.now() + 1000)) return null;
  return {
    ...setup, recipeId: value.recipeId, id: text(value.id, 100) || `restored-${Date.now()}`,
    status: value.status, elapsedMs, startedAt: value.status === 'running' ? startedAt : null,
    cues: value.cues === true, early: value.early === true,
    taste: Object.hasOwn(TASTES, value.taste) ? value.taste : '', notes: text(value.notes, 1000)
  };
}

function safeProfile(value) {
  const result = profileDefault();
  if (!value || value.version !== 1 || typeof value !== 'object') return result;
  for (const recipe of RECIPES) {
    const setup = safeSetup(value.settings?.[recipe.id], recipe.id);
    if (setup) result.settings[recipe.id] = setup;
  }
  result.favorites = [...new Set(Array.isArray(value.favorites) ? value.favorites.filter(knownRecipe) : [])];
  result.lastRecipe = knownRecipe(value.lastRecipe) ? value.lastRecipe : '';
  result.active = safeSession(value.active);
  const ids = new Set();
  result.diary = (Array.isArray(value.diary) ? value.diary : []).slice(0, 100).flatMap(entry => {
    if (!entry || !knownRecipe(entry.recipeId)) return [];
    const setup = safeSetup(entry, entry.recipeId);
    const at = Number(entry.at);
    const id = text(entry.id, 100);
    if (!setup || !id || ids.has(id) || !Number.isFinite(at) || at <= 0 || at > Date.now() + 60000) return [];
    ids.add(id);
    return [{ ...setup, id, recipeId: entry.recipeId, at,
      elapsed: Math.max(0, Math.min(Number(entry.elapsed) || 0, 86400)), early: entry.early === true,
      taste: Object.hasOwn(TASTES, entry.taste) ? entry.taste : '', notes: text(entry.notes, 1000) }];
  });
  return result;
}

export function initBrewCompanion(root) {
  if (!root || root.dataset.ready === 'true') return;
  const $ = name => root.querySelector(`[data-bc-${name}]`);
  const $$ = name => [...root.querySelectorAll(`[data-bc-${name}]`)];
  const panels = $$('panel');
  if (!panels.length || !$('recipes') || !$('dose') || !$('start')) return;

  let profile = profileDefault();
  let storageWorks = true;
  let selected = null;
  let scaled = null;
  let session = null;
  let clock = null;
  let lastStage = -1;
  let removedEntry = null;
  let audio = null;
  let view = 'library';
  let savedFinish = false;
  const announce = message => { $('status').textContent = message; };
  function storageWarning(message) {
    storageWorks = false;
    $('storage-warning').hidden = false;
    $('storage-warning').textContent = message;
    $('storage-note').textContent = 'Your settings and notes are available for this visit only.';
  }
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw && raw.length <= 500000) profile = safeProfile(JSON.parse(raw));
    else if (raw) storageWarning('Saved brewing data was too large to load. You can still brew here.');
  } catch {
    storageWarning('We could not read saved brewing data. You can still use the tools during this visit.');
  }
  function persist() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(profile));
      storageWorks = true;
      $('storage-warning').hidden = true;
      $('storage-note').textContent = 'Saved on this browser only. Clearing browser data removes your settings and notes.';
      return true;
    } catch {
      storageWarning('This browser could not save your brewing data. You can still brew, copy a recipe and export your notes.');
      return false;
    }
  }
  function show(next, focus = true) {
    view = next;
    panels.forEach(panel => { panel.hidden = panel.dataset.bcPanel !== next; });
    const busy = next === 'brew' || next === 'finish';
    $$('view').forEach(control => {
      control.disabled = busy;
      control.setAttribute('aria-pressed', String(control.dataset.bcView === next || (control.dataset.bcView === 'library' && ['setup', 'brew', 'finish'].includes(next))));
    });
    if (focus) {
      const panel = panels.find(item => item.dataset.bcPanel === next);
      const heading = panel?.querySelector('h2,h3');
      if (heading) { heading.tabIndex = -1; heading.focus({ preventScroll: true }); }
      panel?.scrollIntoView({ behavior: 'auto', block: 'start' });
    }
  }
  function currentSetup() {
    return { dose: Number($('dose').value), coffee: text($('coffee').value), grinder: text($('grinder').value), setting: text($('setting').value, 40) };
  }
  function saveSetup() {
    if (!selected || !scaleRecipe(selected.id, $('dose').value).valid) return;
    profile.settings[selected.id] = currentSetup();
    profile.lastRecipe = selected.id;
    persist();
  }
  function toggleFavorite(id, fromLibrary = false) {
    profile.favorites = profile.favorites.includes(id) ? profile.favorites.filter(item => item !== id) : [...profile.favorites, id];
    persist();
    if (selected?.id === id) updateFavorite();
    renderLibrary();
    if (fromLibrary) ($('recipes').querySelector(`[data-bc-save-id="${id}"]`) || $('favorites-only')).focus({ preventScroll: true });
    announce(profile.favorites.includes(id) ? 'Recipe saved to favourites.' : 'Recipe removed from favourites.');
  }
  function updateFavorite() {
    const saved = profile.favorites.includes(selected.id);
    $('favorite').textContent = saved ? 'Saved to favourites' : 'Save recipe';
    $('favorite').setAttribute('aria-pressed', String(saved));
  }
  function renderLibrary() {
    const method = $('filter').value;
    const onlySaved = $('favorites-only').checked;
    const recipes = RECIPES.filter(recipe => (method === 'all' || method === recipe.method) && (!onlySaved || profile.favorites.includes(recipe.id)));
    $('result-count').textContent = recipes.length === 0 ? 'No brewing guides match this view.' : `${recipes.length} brewing ${recipes.length === 1 ? 'guide' : 'guides'}`;
    $('recipes').replaceChildren();
    for (const recipe of recipes) {
      const card = node('article', 'bc-recipe-card');
      card.append(node('p', 'bc-card-method', METHODS[recipe.method] || recipe.method), node('h3', 'bc-card-title', recipe.title), node('p', 'bc-card-summary', recipe.summary));
      const total = recipe.steps.reduce((sum, step) => sum + step.seconds, 0);
      card.append(node('p', 'bc-card-meta', `${recipe.defaultDose} g coffee · ${formatTime(total)} guide · ${recipe.icedFraction ? 'Iced' : 'Hot'}`));
      const actions = node('div', 'bc-card-actions');
      const open = button('Build this brew', () => openRecipe(recipe.id));
      open.setAttribute('aria-label', `Build this brew: ${recipe.title}`);
      const save = button(profile.favorites.includes(recipe.id) ? 'Saved' : 'Save', () => toggleFavorite(recipe.id, true), true);
      save.dataset.bcSaveId = recipe.id;
      save.classList.add('bc-star');
      save.setAttribute('aria-label', `Save ${recipe.title}`);
      save.setAttribute('aria-pressed', String(profile.favorites.includes(recipe.id)));
      actions.append(open, save);
      card.append(actions);
      $('recipes').append(card);
    }
    $('library-empty').hidden = recipes.length > 0;
    $('last').replaceChildren();
    const previous = knownRecipe(profile.lastRecipe);
    $('last').hidden = !previous;
    if (previous) $('last').append(node('p', '', 'Back for another cup?'), button(`Last recipe: ${previous.title}`, () => openRecipe(previous.id), true));
  }
  function renderSetup() {
    scaled = scaleRecipe(selected.id, $('dose').value);
    $('dose-error').textContent = scaled.valid ? '' : scaled.errors.join(' ');
    $('dose-error').hidden = scaled.valid;
    $('dose').setAttribute('aria-invalid', String(!scaled.valid));
    $('start').disabled = !scaled.valid || !$('check-ready').checked;
    $('copy').disabled = !scaled.valid;
    $('share').disabled = !scaled.valid;
    for (const name of ['water', 'ice', 'total']) $(name).textContent = scaled.valid ? `${scaled[{ water: 'hotWater', ice: 'iceWater', total: 'totalWater' }[name]]} g` : '—';
    $('ice-row').hidden = !selected.icedFraction;
    $('schedule').replaceChildren();
    if (scaled.valid) for (const step of scaled.steps) {
      const item = node('li', 'bc-schedule-step');
      item.append(node('strong', '', `${formatTime(step.start)} · ${step.title}`), node('p', '', step.instruction));
      if (step.target !== null) item.append(node('p', 'bc-schedule-target', `Add ${step.add} g · scale total ${step.target} g`));
      $('schedule').append(item);
    }
    return scaled;
  }
  function openRecipe(id, preset = null) {
    if (session) { announce('Finish or stop your current brew before choosing another recipe.'); return; }
    selected = knownRecipe(id);
    if (!selected) return;
    const setup = safeSetup(preset, id) || profile.settings[id] || { dose: selected.defaultDose, coffee: '', grinder: '', setting: '' };
    $('title').textContent = selected.title;
    $('description').textContent = selected.summary;
    $('dose').min = selected.minDose;
    $('dose').max = selected.maxDose;
    $('dose').step = '0.5';
    $('dose').value = setup.dose;
    $('dose-help').textContent = `${selected.minDose}–${selected.maxDose} g, in half-gram steps. Check your brewer's capacity.`;
    for (const name of ['coffee', 'grinder', 'setting']) $(name).value = setup[name] || '';
    $('temperature').textContent = selected.temperature;
    $('grind').textContent = selected.grind;
    $('timing').textContent = `${formatTime(selected.steps.reduce((sum, step) => sum + step.seconds, 0))} timing guide`;
    $('recipe-note').textContent = selected.note;
    for (const [name, items] of [['equipment', selected.equipment], ['preparation', selected.preparation]]) {
      $(name).replaceChildren(...items.map(item => node('li', '', item)));
    }
    $('sources').replaceChildren(...selected.sources.map(source => {
      const li = node('li');
      const link = node('a', '', source.label);
      link.href = source.url;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.setAttribute('aria-label', `${source.label} (opens in a new tab)`);
      li.append(link);
      return li;
    }));
    $('check-ready').checked = false;
    $('share-output').hidden = true;
    updateFavorite();
    renderSetup();
    show('setup');
  }
  const elapsedMs = () => !session ? 0 : Math.max(0, session.elapsedMs + (session.status === 'running' ? Date.now() - session.startedAt : 0));
  function saveSession() { profile.active = session ? { ...session } : null; persist(); }
  function cue() {
    if (!session?.cues || !audio || audio.state !== 'running' || document.hidden) return;
    try {
      const tone = audio.createOscillator();
      const gain = audio.createGain();
      tone.frequency.value = 660;
      gain.gain.setValueAtTime(0.035, audio.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, audio.currentTime + 0.15);
      tone.connect(gain); gain.connect(audio.destination);
      tone.start(); tone.stop(audio.currentTime + 0.16);
    } catch { /* The visible instructions remain sufficient when sound is unavailable. */ }
  }
  function beginAudio() {
    if (!$('cue')?.checked) return;
    try {
      const Audio = window.AudioContext || window.webkitAudioContext;
      if (Audio) { audio ||= new Audio(); void audio.resume().catch(() => {}); }
    } catch { /* No permission request and no dependency on sound. */ }
  }
  function renderBrew() {
    if (!session || session.status === 'complete') return;
    const stage = getBrewStage(scaled, elapsedMs() / 1000);
    if (stage.complete) { finish(false); return; }
    $('brew-title').textContent = selected.title;
    $('step-label').textContent = `Step ${stage.index + 1} of ${scaled.steps.length}`;
    $('step-title').textContent = stage.step.title;
    $('instruction').textContent = stage.step.instruction;
    $('elapsed').textContent = formatTime(stage.elapsed);
    $('countdown').textContent = formatTime(stage.remaining);
    $('progress').value = Math.min(100, stage.elapsed / stage.total * 100);
    $('pour-row').hidden = stage.step.target === null;
    $('add').textContent = stage.step.add === null ? '—' : `${stage.step.add} g`;
    $('target').textContent = stage.step.target === null ? '—' : `${stage.step.target} g`;
    const next = scaled.steps[stage.index + 1];
    $('next').textContent = next ? `${next.title}. ${next.instruction}${next.target === null ? '' : ` Scale total: ${next.target} g.`}` : 'Complete the last step, then taste when the coffee is comfortable to drink.';
    $('pause').textContent = session.status === 'running' ? 'Pause timer' : 'Resume timer';
    if (stage.index !== lastStage) {
      lastStage = stage.index;
      announce(`Step ${stage.index + 1}: ${stage.step.title}. ${stage.step.instruction}${stage.step.target === null ? '' : ` Add ${stage.step.add} grams, to ${stage.step.target} grams on your scale.`}`);
      if (stage.index > 0) cue();
    }
  }
  function runClock() { clearInterval(clock); clock = setInterval(renderBrew, 250); renderBrew(); }
  function start() {
    if (session || !renderSetup().valid || !$('check-ready').checked) return;
    saveSetup();
    beginAudio();
    session = { ...currentSetup(), recipeId: selected.id,
      id: window.crypto?.randomUUID?.() || `brew-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      status: 'running', elapsedMs: 0, startedAt: Date.now(), cues: Boolean($('cue')?.checked), early: false, taste: '', notes: '' };
    lastStage = -1;
    $('stop-confirm').hidden = true;
    if ($('resume-note')) $('resume-note').hidden = true;
    saveSession();
    show('brew');
    runClock();
  }
  function pauseOrResume() {
    if (!session || session.status === 'complete') return;
    if (session.status === 'running') {
      session.elapsedMs = elapsedMs();
      session.startedAt = null;
      session.status = 'paused';
      announce('Timer paused.');
    } else {
      session.startedAt = Date.now();
      session.status = 'running';
      if ($('resume-note')) $('resume-note').hidden = true;
      announce('Timer resumed.');
    }
    saveSession();
    renderBrew();
  }
  function finish(early = true) {
    if (!session) return;
    session.elapsedMs = Math.min(elapsedMs(), scaled.totalSeconds * 1000);
    session.startedAt = null;
    session.status = 'complete';
    session.early = early;
    clearInterval(clock);
    clock = null;
    savedFinish = false;
    $('another').textContent = 'Choose another recipe';
    $('finish-title').textContent = early ? 'Your brew, your timing.' : 'The timer is done. How is the coffee?';
    $('finish-summary').textContent = `${selected.title} · ${session.dose} g coffee · ${scaled.hotWater} g hot water${scaled.iceWater ? ` + ${scaled.iceWater} g ice` : ''} · ${formatTime(session.elapsedMs / 1000)}${early ? ' (finished early)' : ''}. Finish any pouring, draining or pressing before serving.`;
    $('taste').value = session.taste || '';
    $('notes').value = session.notes || '';
    $('advice').textContent = TASTES[$('taste').value] || NEUTRAL_ADVICE;
    $('save').disabled = profile.diary.some(entry => entry.id === session.id);
    $('save-status').textContent = '';
    saveSession();
    show('finish');
    announce(early ? 'Timer stopped early. You can save a note about this brew.' : 'Timing guide complete. Finish any pouring, draining or pressing before serving.');
    if (!early) cue();
  }
  function clearSession() {
    clearInterval(clock); clock = null; session = null; profile.active = null; lastStage = -1;
    persist();
  }
  function saveBrew() {
    if (!session || session.status !== 'complete' || profile.diary.some(entry => entry.id === session.id)) return;
    session.taste = $('taste').value;
    session.notes = text($('notes').value, 1000);
    profile.diary.unshift({ id: session.id, recipeId: session.recipeId, dose: session.dose,
      coffee: session.coffee, grinder: session.grinder, setting: session.setting, at: Date.now(),
      elapsed: Math.round(session.elapsedMs / 1000), early: session.early, taste: session.taste, notes: session.notes });
    profile.diary = profile.diary.slice(0, 100);
    profile.active = null;
    const saved = persist();
    $('save').disabled = true;
    $('save-status').textContent = saved ? 'Saved in My brews on this browser.' : 'Added for this visit. Export My brews to keep a copy.';
    announce($('save-status').textContent);
    renderDiary();
    savedFinish = true;
    session = null;
    $$('view').forEach(control => { control.disabled = false; });
    $('another').textContent = 'View My brews';
  }
  function renderDiary() {
    $('diary-list').replaceChildren();
    $('diary-empty').hidden = profile.diary.length > 0;
    $('export').disabled = profile.diary.length === 0;
    for (const entry of profile.diary) {
      const recipe = knownRecipe(entry.recipeId);
      if (!recipe) continue;
      const item = node('article', 'bc-diary-entry');
      item.append(node('h3', 'bc-diary-title', recipe.title), node('p', 'bc-diary-meta', `${new Date(entry.at).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })} · ${entry.dose} g coffee · ${formatTime(entry.elapsed)}${entry.early ? ' · finished early' : ''}`));
      if (entry.coffee) item.append(node('p', '', `Coffee: ${entry.coffee}`));
      if (entry.grinder || entry.setting) item.append(node('p', '', `Grinder: ${[entry.grinder, entry.setting].filter(Boolean).join(' · ')}`));
      item.append(node('p', '', `Taste: ${({ balanced: 'Happy with it', strong: 'Too intense', weak: 'Too light', sour: 'Sharp or sour', bitter: 'Bitter or drying' })[entry.taste] || 'Not recorded'}`));
      if (entry.notes) item.append(node('p', 'bc-diary-note', entry.notes));
      const actions = node('div', 'bc-diary-actions');
      const remove = button('Remove note', () => {
        removedEntry = { entry, index: profile.diary.indexOf(entry) };
        profile.diary = profile.diary.filter(item => item.id !== entry.id);
        persist(); renderDiary(); $('undo-area').hidden = false; $('undo').focus({ preventScroll: true }); announce('Brew note removed. Undo is available until you leave this page.');
      }, true);
      remove.dataset.bcRemoveId = entry.id;
      actions.append(button('Brew again', () => openRecipe(entry.recipeId, entry)), remove);
      item.append(actions);
      $('diary-list').append(item);
    }
  }
  function exportDiary() {
    const payload = { exportedAt: new Date().toISOString(), format: 'Hidden Grounds brew diary v1', brews: profile.diary };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = node('a');
    link.href = url; link.download = 'hidden-grounds-my-brews.json';
    root.append(link); link.click(); link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    announce('Brew diary exported. Keep this file if you plan to clear browser data.');
  }
  function recipeText() {
    const result = renderSetup();
    if (!result.valid) return '';
    return [`Hidden Grounds · ${selected.title}`, `${result.dose} g coffee; ${result.hotWater} g hot water${result.iceWater ? `; ${result.iceWater} g ice` : ''}.`, `${selected.grind}. ${selected.temperature}.`,
      ...selected.preparation, ...result.steps.map(step => `${formatTime(step.start)} ${step.title}: ${step.instruction}${step.target === null ? '' : ` Add ${step.add} g; total on scale ${step.target} g.`}`), selected.note,
      ...selected.sources.map(source => `${source.label}: ${source.url}`)].join('\n');
  }
  async function copyText(value, success) {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      $('share-output').hidden = true;
      announce(success);
    } catch {
      $('share-output').value = value;
      $('share-output').hidden = false;
      $('share-output').focus(); $('share-output').select();
      announce('Copy is unavailable here. Your text is selected below so you can copy it yourself.');
    }
  }
  function shareRecipe() {
    if (!renderSetup().valid) return;
    const url = new URL(location.origin + location.pathname);
    url.searchParams.set('brew', selected.id);
    url.searchParams.set('dose', String(scaled.dose));
    url.hash = 'brew-bench';
    void copyText(url.href, 'Recipe link copied. It includes only the recipe and coffee amount.');
  }

  $$('view').forEach(control => control.addEventListener('click', () => {
    if (session) { announce('Finish or stop your brew before leaving the timer.'); return; }
    if (control.dataset.bcView === 'library') renderLibrary();
    if (control.dataset.bcView === 'diary') renderDiary();
    show(control.dataset.bcView);
  }));
  $('filter').addEventListener('change', renderLibrary);
  $('favorites-only').addEventListener('change', renderLibrary);
  $('favorite').addEventListener('click', () => toggleFavorite(selected.id));
  $('back').addEventListener('click', () => { renderLibrary(); show('library'); });
  $('dose').addEventListener('input', () => { renderSetup(); $('share-output').hidden = true; });
  for (const name of ['dose', 'coffee', 'grinder', 'setting']) {
    $(name).addEventListener('input', saveSetup);
    $(name).addEventListener('change', saveSetup);
  }
  $('check-ready').addEventListener('change', renderSetup);
  $('start').addEventListener('click', start);
  $('pause').addEventListener('click', pauseOrResume);
  $('finish-early').addEventListener('click', () => finish(true));
  $('stop').addEventListener('click', () => { $('stop-confirm').hidden = false; $('stop-no').focus(); });
  $('stop-no').addEventListener('click', () => { $('stop-confirm').hidden = true; $('stop').focus(); });
  $('stop-yes').addEventListener('click', () => { clearSession(); renderLibrary(); show('library'); announce('Timer stopped. Your recipe settings are kept.'); });
  $('taste').addEventListener('change', () => {
    $('advice').textContent = TASTES[$('taste').value] || NEUTRAL_ADVICE;
    if (session) { session.taste = $('taste').value; if (!$('save').disabled) saveSession(); }
  });
  $('notes').addEventListener('input', () => { if (session && !$('save').disabled) { session.notes = text($('notes').value, 1000); saveSession(); } });
  $('save').addEventListener('click', saveBrew);
  $('another').addEventListener('click', () => {
    const destination = savedFinish ? 'diary' : 'library';
    clearSession(); renderLibrary(); renderDiary(); show(destination);
  });
  $('undo').addEventListener('click', () => {
    if (!removedEntry) return;
    const restoredId = removedEntry.entry.id;
    profile.diary.splice(removedEntry.index, 0, removedEntry.entry);
    profile.diary = profile.diary.slice(0, 100); removedEntry = null;
    persist(); renderDiary(); $('undo-area').hidden = true;
    [...$('diary-list').querySelectorAll('[data-bc-remove-id]')].find(button => button.dataset.bcRemoveId === restoredId)?.focus({ preventScroll: true });
    announce('Brew note restored.');
  });
  $('export').addEventListener('click', exportDiary);
  $('copy').addEventListener('click', () => { void copyText(recipeText(), 'Brew card copied.'); });
  $('share').addEventListener('click', shareRecipe);
  $('ey-calculate').addEventListener('click', () => {
    const result = extractionYield($('ey-dose').value, $('ey-beverage').value, $('ey-tds').value);
    $('ey-output').textContent = result.valid ? `${result.value.toFixed(1)}% extraction yield` : '—';
    $('ey-error').textContent = result.valid ? '' : result.errors.join(' ');
    $('ey-error').hidden = result.valid;
    announce(result.valid ? $('ey-output').textContent : $('ey-error').textContent);
  });
  for (const name of ['ey-dose', 'ey-beverage', 'ey-tds']) $(name).addEventListener('input', () => { $('ey-output').textContent = '—'; $('ey-error').hidden = true; });
  document.addEventListener('visibilitychange', () => { if (!document.hidden && session?.status === 'running') renderBrew(); });
  window.addEventListener('pagehide', () => { if (session && !profile.diary.some(entry => entry.id === session.id)) saveSession(); });

  const openQuickCalculator = () => {
    if (!['#quick-calculator', '#brew-card', '#quick-ratio-tool'].includes(location.hash)) return;
    const quick = document.getElementById('quick-calculator');
    if (quick) { quick.open = true; document.querySelector(location.hash)?.scrollIntoView({ block: 'start' }); }
  };
  window.addEventListener('hashchange', openQuickCalculator);
  document.querySelectorAll('a[href="#quick-calculator"]').forEach(link => link.addEventListener('click', () => {
    const quick = document.getElementById('quick-calculator'); if (quick) quick.open = true;
  }));
  openQuickCalculator();

  root.dataset.ready = 'true';
  const enhanced = root.querySelector('[data-bc-enhanced]');
  if (enhanced) enhanced.hidden = false;
  const fallback = root.querySelector('[data-bc-fallback]');
  if (fallback) fallback.hidden = true;
  renderLibrary(); renderDiary(); show('library', false);
  if (profile.active) {
    session = profile.active;
    selected = knownRecipe(session.recipeId);
    scaled = scaleRecipe(selected.id, session.dose);
    const wasComplete = session.status === 'complete';
    session.elapsedMs = Math.min(elapsedMs(), scaled.totalSeconds * 1000);
    session.startedAt = null;
    session.status = 'paused';
    if (wasComplete || session.elapsedMs >= scaled.totalSeconds * 1000) finish(session.early);
    else {
      saveSession(); show('brew', false); runClock();
      if ($('resume-note')) { $('resume-note').textContent = 'Your previous timer is paused. Check your brew, then resume when you are ready.'; $('resume-note').hidden = false; }
      announce('Previous brew restored with the timer paused.');
    }
  } else {
    const params = new URLSearchParams(location.search);
    const id = params.get('brew');
    if (id) {
      const recipe = knownRecipe(id);
      const result = recipe && scaleRecipe(id, params.has('dose') ? params.get('dose') : recipe.defaultDose);
      if (result?.valid) openRecipe(id, { dose: result.dose });
      else announce('That shared recipe link has an unsupported recipe or amount. Choose a recipe below to get started.');
    }
  }
}

const init = () => document.querySelectorAll('[data-brew-companion]').forEach(initBrewCompanion);
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
else init();
